// Backdoor: ?free=1 = simulate free user, ?free=0 = premium (no console paste needed). Only when not on production.
import { applyPresentationBootstrap, isOfflineLocalServer } from './utils/presentationMode.js';

if (typeof window !== 'undefined' && !window.location.hostname.includes('supercalcpro')) {
  applyPresentationBootstrap();
  if (isOfflineLocalServer() && 'serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => {});
  }
  const params = new URLSearchParams(window.location.search);
  const free = params.get('free');
  if (free === '1') {
    localStorage.setItem('simulate_free_user', '1');
    localStorage.removeItem('subscription_tier');
    localStorage.removeItem('subscription_expiry');
    localStorage.removeItem('subscription_id');
    localStorage.removeItem('subscription_start');
    window.location.replace(window.location.pathname + (window.location.hash || ''));
  } else if (free === '0') {
    localStorage.removeItem('simulate_free_user');
    window.location.replace(window.location.pathname + (window.location.hash || ''));
  }
}

import React from 'react';
import { createRoot } from 'react-dom/client';
import { initABTests } from './utils/abTest';
import { initAnalytics } from './utils/analytics';
import { captureAttribution } from './utils/attribution';
import './tailwind.css';
import './styles/mobile-enhancements.css';
import './styles/advanced-mobile-accordion.css';
import './styles/iphone-calculator-fixes.css';
import MainNavigation from './MainNavigation';
import GlobalPricingModal from './components/GlobalPricingModal';
import ErrorBoundary from './ErrorBoundary';
import { captureError, captureMessage } from './SentryConfig'; // Initialize Sentry error monitoring
import { GlobalErrorHandler } from './GlobalErrorHandler';
import { LoggingManager } from './LoggingManager';
import { AnalyticsManager } from './AnalyticsManager';
import { AuthManager } from './AuthManager';
import { DataPersistenceManager } from './DataPersistenceManager';
import { SecurityManager } from './SecurityManager';
import { ComplianceManager } from './ComplianceManager';
import { DatabaseManager } from './DatabaseManager';
import { PaymentManager } from './PaymentManager';
import { SubscriptionManager } from './SubscriptionManager';
import { MonitoringManager } from './MonitoringManager';
import { getFinancialConfig } from './FinancialConfig.js';
// Sync-hydrate Age Pension schedules from financial-rates.json before first paint/calc.
import './financial-config-hydrate-from-json.js';
// TEMPORARILY DISABLED - Service worker causing caching issues with Stripe
// import { register, registerInstallPrompt } from './serviceWorkerRegistration';
// import './serviceWorker'; // Import enhanced service worker

// Initialize all production systems
initAnalytics();
captureAttribution();
GlobalErrorHandler.initialize();
LoggingManager.initialize();
AnalyticsManager.initialize();
AuthManager.initialize();
DataPersistenceManager.initialize();
SecurityManager.initialize();
ComplianceManager.initialize();
DatabaseManager.initialize();
PaymentManager.initialize();
SubscriptionManager.initialize();
MonitoringManager.initialize();

// Log application startup
LoggingManager.info('Application starting', {
  version: '1.0.0',
  environment: (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) || 'production',
  timestamp: new Date().toISOString()
});

// Track application startup
AnalyticsManager.trackEvent('application_start', {
  version: '1.0.0',
  environment: (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) || 'production'
});

// Assign A/B test variants before first render
initABTests();

const container = document.getElementById('root');
const root = createRoot(container);

const renderApp = () => {
  root.render(
    <ErrorBoundary>
      <MainNavigation />
      <GlobalPricingModal />
    </ErrorBoundary>
  );
};

// Load live rates from financial-rates.json before first paint (Advanced + SMSF share FinancialConfig).
// Offline demo (:8765): paint immediately — waiting on fetch feels like a blank page flash.
if (typeof window !== 'undefined' && isOfflineLocalServer()) {
  renderApp();
  getFinancialConfig()
    .then(() => LoggingManager.info('Financial rates loaded from JSON'))
    .catch((err) => {
      LoggingManager.warn('Financial rates load failed; using fallback config', { error: String(err) });
    });
} else {
  getFinancialConfig()
    .then(() => {
      LoggingManager.info('Financial rates loaded from JSON');
      renderApp();
    })
    .catch((err) => {
      LoggingManager.warn('Financial rates load failed; using fallback config', { error: String(err) });
      renderApp();
    });
}

// TEMPORARILY DISABLED - Service worker causing caching issues
// register();
// registerInstallPrompt();

// Log successful initialization
LoggingManager.info('Application initialized successfully');
AnalyticsManager.trackEvent('application_ready');
